import { useMemo, useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { devLog } from "@/lib/log";
import { resizeImages } from "@/lib/resizeImage";
import { toast } from "sonner";
import { Paperclip, FileText, ImageIcon, Trash2, Plus, Loader2, Sparkles, Camera, Files } from "lucide-react";
import AttachmentPreview from "@/components/AttachmentPreview";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf";
const SCANNABLE = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "application/pdf",
]);

const FIELD_LABEL = {
  supplier: "Supplier",
  total: "Total",
  gst: "GST",
  entry_date: "Date",
  description: "Description",
  deduction_category: "Category",
  category: "Category",
  cost_code: "Cost code",
  notes: "Notes",
};

/**
 * Attachments panel for an entry OR a job.
 * Pass `endpoint` = `/entries/{id}/attachments` or `/jobs/{id}/attachments`.
 *
 * Attachment OCR (entries only):
 *   - Auto-runs silently on FIRST photo/PDF attached (fills empty fields only).
 *   - Manual "Scan with AI" button available on every scannable tile thereafter.
 *   - Filled fields are bubbled up via onOcrApplied(parsed, filled[], updatedEntry).
 */
export default function AttachmentsPanel({
  entryId,
  jobId,
  attachments = [],
  onChange,
  onOcrApplied,
  // ---- Draft-mode (attach BEFORE save) ----------------------------------
  // Parent-controlled list of already-resized File objects that will be
  // uploaded when the parent creates the entry/job. If BOTH `draftFiles`
  // AND `onDraftFilesChange` are provided, the "Add" tile becomes live
  // even when there's no parentId — files land in the drafts array and
  // render inline as tiles. Parent is responsible for calling
  // `uploadDraftAttachments(id, files)` after its create-API returns and
  // for keeping the drafts around if that upload fails so the tradie
  // doesn't have to re-pick from the OS gallery.
  draftFiles = null,
  onDraftFilesChange = null,
  testidPrefix = "rv",
}) {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [scanningId, setScanningId] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(null);
  // Dark "choose source" sheet — replaces the OS's pale native picker menu
  // ("Photo Library / Take Photo / Choose Files") on mobile Safari. Each row
  // fires a hidden `<input type="file">` configured for that source.
  const [sourceSheetOpen, setSourceSheetOpen] = useState(false);
  // Dark themed remove-confirm — replaces the default `window.confirm` (blue
  // iOS action button) so the tradie sees BuildIntel's language everywhere.
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const parentId = entryId || jobId;
  const draftMode = !parentId && typeof onDraftFilesChange === "function";
  const drafts = Array.isArray(draftFiles) ? draftFiles : [];
  const kind = entryId ? "entry" : "job";
  const endpoint = entryId
    ? `/entries/${entryId}/attachments`
    : jobId
    ? `/jobs/${jobId}/attachments`
    : null;

  // Object URLs for draft-file previews — recomputed only when the draft
  // list identity changes. Revoked on the very next render / unmount so
  // rapid-fire "Add → Remove → Add" doesn't leak blob:...  handles.
  const draftPreviews = useMemo(
    () => drafts.map((f, i) => ({
      key: `draft-${i}-${f.name}-${f.size}-${f.lastModified || 0}`,
      name: f.name,
      isImage: (f.type || "").startsWith("image/"),
      url: (f.type || "").startsWith("image/") ? URL.createObjectURL(f) : null,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drafts]
  );
  useEffect(() => () => {
    // Cleanup revokes ALL object URLs when the previews array changes or
    // the panel unmounts. Safari holds these forever otherwise.
    draftPreviews.forEach((p) => { if (p.url) URL.revokeObjectURL(p.url); });
  }, [draftPreviews]);

  const runOcr = async (attachmentId, { silent = false, overwrite = false } = {}) => {
    if (!entryId || !attachmentId) return;
    setScanningId(attachmentId);
    try {
      const { data } = await api.post(
        `/entries/${entryId}/attachments/${attachmentId}/ocr`,
        null,
        { params: { apply_empty_only: !overwrite } }
      );
      const filled = data.filled || [];
      onOcrApplied?.(data.parsed || {}, filled, data.entry || null);
      if (filled.length) {
        const labels = filled.map((f) => FIELD_LABEL[f] || f).slice(0, 3).join(", ");
        toast.success(`AI filled ${filled.length} field${filled.length === 1 ? "" : "s"} from attachment: ${labels}`);
      } else if (!silent) {
        toast("Nothing new to fill — form already looks good.");
      }
    } catch (e) {
      devLog.warn("attachment ocr failed", e);
      if (!silent) toast.error("Scan failed. Try again.");
    } finally {
      setScanningId(null);
    }
  };

  const upload = async (files) => {
    // First-line dedupe: strip File objects the tradie picked twice in the
    // same OS picker session (iOS Photo Library lets you tap the same
    // thumbnail multiple times). Compare by `name+size` — same file will
    // always match, and different files with identical name+size are so
    // rare on a phone gallery that the false-positive risk is negligible.
    const listAll = Array.from(files || []);
    const dupTracker = new Set();
    const list = listAll.filter((f) => {
      const key = `${f.name}::${f.size}`;
      if (dupTracker.has(key)) return false;
      dupTracker.add(key);
      return true;
    });
    if (!list.length) return;

    // ---- Draft mode: entry/job doesn't exist yet ------------------------
    // Resize images NOW so we don't re-do it on save, then hand them to
    // the parent as File objects. The parent is responsible for uploading
    // them after its create-API returns.
    if (draftMode) {
      setBusy(true);
      try {
        const resized = await resizeImages(list, { maxEdge: 1200, quality: 0.70 });
        // Dedupe against existing drafts by name+size too — otherwise
        // reopening the picker lets the same photo pile in twice.
        const existing = new Set(drafts.map((f) => `${f.name}::${f.size}`));
        const fresh = resized.filter((f) => !existing.has(`${f.name}::${f.size}`));
        const next = [...drafts, ...fresh];
        onDraftFilesChange(next);
        if (fresh.length) {
          toast.success(`Attached ${fresh.length} file${fresh.length === 1 ? "" : "s"} — uploads when you save`);
        } else {
          toast.message("Already attached — nothing new added");
        }
      } catch (e) {
        devLog.warn("draft resize failed", e);
        toast.error("Attach failed");
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!parentId || !endpoint) {
      // Panel was mounted with neither a parentId nor a draft handler.
      // Nothing sensible we can do — swallow silently so we don't spam
      // the tradie with a confusing "save first" toast on stale mounts.
      return;
    }

    const clientSkipped = listAll.length - list.length;
    const wasEmpty = attachments.length === 0;
    setBusy(true);
    try {
      // Downsize every image on the device before it hits the wire —
      // non-image files (PDFs, CSVs) pass through untouched. Saves ~90% of
      // bytes for on-site phone photos and keeps the accountant-pack
      // appendix pages a manageable size later.
      const resized = await resizeImages(list, { maxEdge: 1200, quality: 0.70 });
      const fd = new FormData();
      resized.forEach((f) => fd.append("files", f));
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.([...(attachments || []), ...(data.added || [])]);
      // Combined dedupe report — server-side (byte-hash) + client-side
      // (filename+size). One clear toast so tradies aren't confused by
      // "attached 2 files" when they picked 3.
      const serverSkipped = Array.isArray(data.skipped_duplicates) ? data.skipped_duplicates.length : 0;
      const totalSkipped = clientSkipped + serverSkipped;
      if (data.added.length) {
        let msg = `Attached ${data.added.length} file${data.added.length === 1 ? "" : "s"}`;
        if (totalSkipped) msg += ` · ${totalSkipped} duplicate${totalSkipped === 1 ? "" : "s"} skipped`;
        toast.success(msg);
      } else if (totalSkipped) {
        toast.message(`Skipped ${totalSkipped} duplicate${totalSkipped === 1 ? "" : "s"} — already attached`);
      }

      // Auto-OCR on the very first entry attachment (silent, empty fields only)
      if (entryId && wasEmpty && Array.isArray(data.added) && data.added.length) {
        const firstScannable = data.added.find((a) =>
          SCANNABLE.has((a.content_type || "").toLowerCase())
        );
        if (firstScannable) {
          runOcr(firstScannable.id, { silent: true });
        }
      }
    } catch (e) {
      devLog.warn("upload attachments failed", e);
      const detail = e.response?.data?.detail || "Attach failed";
      toast.error(typeof detail === "string" ? detail : "Attach failed");
    } finally {
      setBusy(false);
    }
  };

  // Draft-only remove — parent state edit, no API call.
  const removeDraft = (index) => {
    if (!draftMode) return;
    const next = drafts.filter((_, i) => i !== index);
    onDraftFilesChange(next);
  };

  const remove = async (att) => {
    if (!parentId || !endpoint) return;
    // Delegate confirm to the dark AlertDialog; actual DELETE fires from
    // `confirmRemoval` when the user accepts.
    setPendingRemoval(att);
  };

  const confirmRemoval = async () => {
    const att = pendingRemoval;
    setPendingRemoval(null);
    if (!att || !parentId || !endpoint) return;
    try {
      await api.delete(`${endpoint}/${att.id}`);
      onChange?.((attachments || []).filter((a) => a.id !== att.id));
      toast("Attachment removed");
    } catch (e) {
      devLog.warn("delete attachment failed", e);
      toast.error("Remove failed");
    }
  };

  const isScannable = (a) => SCANNABLE.has((a.content_type || "").toLowerCase());

  return (
    <>
      <div className="bi-surface p-3" data-testid={`${testidPrefix}-attachments`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-heading inline-flex items-center gap-1.5">
            <Paperclip className="w-3 h-3" /> Attachments{" "}
            {attachments.length > 0 && <span className="text-white/70">· {attachments.length}</span>}
          </div>
          {/* Three hidden file inputs — one per source. Each is triggered by
              the dark source-sheet below to bypass the OS's pale native
              picker menu. `capture="environment"` opens the camera directly
              on mobile Safari/Chrome; the library input drops the `capture`
              so iOS shows the Photos app. The final `inputRef` still exists
              for the legacy testid-based automated tests. */}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              upload(e.target.files);
              e.target.value = "";
            }}
            data-testid={`${testidPrefix}-attach-input`}
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
            data-testid={`${testidPrefix}-attach-camera-input`}
          />
          <input
            ref={libraryRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
            data-testid={`${testidPrefix}-attach-library-input`}
          />
          <button
            type="button"
            onClick={() => setSourceSheetOpen(true)}
            disabled={busy || (!parentId && !draftMode)}
            data-testid={`${testidPrefix}-attach-add`}
            className="h-9 px-3 rounded-lg bi-ghost-btn text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Add
          </button>
        </div>

        {/* Draft-mode hint — no gate-keeping copy. Only shown when the
            tradie has NOT yet attached anything so it doesn't crowd the
            tile grid. */}
        {draftMode && drafts.length === 0 && attachments.length === 0 && (
          <p
            data-testid={`${testidPrefix}-attach-draft-hint`}
            className="text-xs text-white/50"
          >
            Attached photos upload when you save.
          </p>
        )}

        {/* Upload-in-flight indicator — "Securing your data…" gives tradies
            on flaky 4G a clear signal something is happening instead of a
            silent spinner. Progress is indeterminate (marches L→R) because
            we don't have byte-level XHR progress wired up. */}
        {busy && (
          <div
            data-testid={`${testidPrefix}-attach-progress`}
            className="mt-1 space-y-1"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-heading inline-flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Securing your data…
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
              <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-[#FF9500] to-[#FFB86C] bi-progress-march" />
            </div>
          </div>
        )}

        {parentId && attachments.length === 0 && !draftMode && (
          <p className="text-xs text-white/50">
            {kind === "entry"
              ? "No extra files. Attach a PDF tax invoice or photo — AI auto-fills empty fields."
              : "No site documents yet. Attach SWMS PDFs, permits, or site photos."}
          </p>
        )}

        {(attachments.length > 0 || drafts.length > 0) && (
          <div className="grid grid-cols-3 gap-2">
            {/* Draft tiles — rendered FIRST so the tradie sees what they
                just picked, and can remove any before Save. Each tile has
                a "Uploads on save" mini-label so the pending state is
                obvious. */}
            {draftPreviews.map((p, idx) => (
              <div
                key={p.key}
                data-testid={`${testidPrefix}-attach-draft-${idx}`}
                className="relative group bi-surface-2 rounded-lg overflow-hidden aspect-square border border-[#FF9500]/30"
              >
                {p.isImage && p.url ? (
                  <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-[#FF9500] gap-1 p-2">
                    <FileText className="w-8 h-8" />
                    <span className="text-[10px] text-white/70 truncate max-w-full">{p.name}</span>
                  </div>
                )}
                <span className="absolute bottom-1 left-1 h-6 px-2 rounded-full bg-black/70 text-[#FF9500] text-[9px] font-heading font-bold uppercase tracking-wider inline-flex items-center">
                  Uploads on save
                </span>
                <button
                  type="button"
                  onClick={() => removeDraft(idx)}
                  data-testid={`${testidPrefix}-attach-draft-del-${idx}`}
                  className="absolute top-1 right-1 w-8 h-8 rounded-full bg-black/70 text-red-300 flex items-center justify-center opacity-100 transition"
                  title="Remove"
                  aria-label="Remove attachment"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}

            {attachments.map((a, idx) => {
              const scannable = kind === "entry" && isScannable(a);
              const isScanning = scanningId === a.id;
              return (
                <div
                  key={a.id}
                  data-testid={`${testidPrefix}-attach-${a.id}`}
                  className="relative group bi-surface-2 rounded-lg overflow-hidden aspect-square"
                >
                  <button
                    type="button"
                    onClick={() => setPreviewIdx(idx)}
                    data-testid={`${testidPrefix}-attach-open-${a.id}`}
                    className="w-full h-full text-left"
                  >
                    {a.kind === "photo" ? (
                      <img
                        src={`${API_BASE}/api/files/${a.path}`}
                        alt={a.filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-[#FF9500] gap-1 p-2">
                        <FileText className="w-8 h-8" />
                        <span className="text-[10px] text-white/70 truncate max-w-full">
                          {a.filename}
                        </span>
                      </div>
                    )}
                  </button>

                  {scannable && (
                    <button
                      type="button"
                      onClick={() => runOcr(a.id)}
                      disabled={isScanning}
                      data-testid={`${testidPrefix}-attach-ocr-${a.id}`}
                      className="absolute bottom-1 left-1 h-7 px-2 rounded-full bg-black/70 text-[#FF9500] text-[10px] font-medium inline-flex items-center gap-1 opacity-90 hover:opacity-100 active:scale-95 transition"
                      title="Scan with AI"
                    >
                      {isScanning ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Sparkles className="w-3 h-3" />
                      )}
                      {isScanning ? "Scanning" : "Scan"}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => remove(a)}
                    data-testid={`${testidPrefix}-attach-del-${a.id}`}
                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-red-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    title="Remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {previewIdx !== null && (
        <AttachmentPreview
          items={attachments}
          startIndex={previewIdx}
          onClose={() => setPreviewIdx(null)}
        />
      )}

      {/* Dark "choose source" sheet — replaces the OS's pale system menu. */}
      <Sheet open={sourceSheetOpen} onOpenChange={setSourceSheetOpen}>
        <SheetContent
          side="bottom"
          className="bg-[#1F1F1F] border-t border-white/10 text-white rounded-t-2xl"
        >
          <SheetHeader className="text-left">
            <SheetTitle className="font-heading">Add attachment</SheetTitle>
            <SheetDescription className="text-white/50">
              Choose a source. Photos and PDFs (up to 20&nbsp;MB each) are supported.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 grid gap-2 pb-[env(safe-area-inset-bottom)]">
            <button
              type="button"
              data-testid={`${testidPrefix}-attach-source-camera`}
              onClick={() => { setSourceSheetOpen(false); cameraRef.current?.click(); }}
              className="w-full h-14 px-4 rounded-xl bi-surface hover:brightness-110 active:scale-[0.99] transition flex items-center gap-3 border border-transparent hover:border-[#FF9500]/30"
            >
              <span className="w-9 h-9 rounded-lg bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center justify-center">
                <Camera className="w-4 h-4" />
              </span>
              <span className="flex-1 text-left">
                <div className="text-sm font-heading font-bold">Take Photo</div>
                <div className="text-[11px] text-white/50">Opens the camera on your phone</div>
              </span>
            </button>
            <button
              type="button"
              data-testid={`${testidPrefix}-attach-source-library`}
              onClick={() => { setSourceSheetOpen(false); libraryRef.current?.click(); }}
              className="w-full h-14 px-4 rounded-xl bi-surface hover:brightness-110 active:scale-[0.99] transition flex items-center gap-3 border border-transparent hover:border-[#FF9500]/30"
            >
              <span className="w-9 h-9 rounded-lg bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center justify-center">
                <ImageIcon className="w-4 h-4" />
              </span>
              <span className="flex-1 text-left">
                <div className="text-sm font-heading font-bold">Photo Library</div>
                <div className="text-[11px] text-white/50">Pick one or more photos from your gallery</div>
              </span>
            </button>
            <button
              type="button"
              data-testid={`${testidPrefix}-attach-source-files`}
              onClick={() => { setSourceSheetOpen(false); inputRef.current?.click(); }}
              className="w-full h-14 px-4 rounded-xl bi-surface hover:brightness-110 active:scale-[0.99] transition flex items-center gap-3 border border-transparent hover:border-[#FF9500]/30"
            >
              <span className="w-9 h-9 rounded-lg bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center justify-center">
                <Files className="w-4 h-4" />
              </span>
              <span className="flex-1 text-left">
                <div className="text-sm font-heading font-bold">Choose Files</div>
                <div className="text-[11px] text-white/50">PDF tax invoices, SWMS docs, permits…</div>
              </span>
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Dark "remove attachment?" dialog — no default iOS blue action. */}
      <AlertDialog
        open={!!pendingRemoval}
        onOpenChange={(v) => { if (!v) setPendingRemoval(null); }}
      >
        <AlertDialogContent className="bg-[#1F1F1F] border border-white/10 text-white rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remove this attachment?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              This can&apos;t be undone. The file will be detached from this{" "}
              {kind} immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              data-testid={`${testidPrefix}-attach-remove-cancel`}
              className="bi-ghost-btn text-white hover:brightness-110 border-white/10"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid={`${testidPrefix}-attach-remove-confirm`}
              onClick={confirmRemoval}
              className="bg-red-500/90 hover:bg-red-500 text-white font-heading font-bold"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function AttachmentBadges({ entry }) {
  const atts = entry?.attachments || [];
  const photos = atts.filter((a) => a.kind === "photo").length;
  const docs = atts.filter((a) => a.kind !== "photo").length;
  const hasVoice = !!entry?.voice_path;
  if (!photos && !docs && !hasVoice) return null;
  return (
    <div className="flex items-center gap-1" data-testid={`entry-attach-badge-${entry.id}`}>
      {photos > 0 && (
        <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/5 text-white/60">
          <ImageIcon className="w-3 h-3" /> {photos}
        </span>
      )}
      {docs > 0 && (
        <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/5 text-white/60">
          <FileText className="w-3 h-3" /> {docs}
        </span>
      )}
    </div>
  );
}

/**
 * uploadDraftAttachments — parent-side helper called AFTER the parent
 * has created its entry / job and knows the resulting id. Uploads the
 * File objects held on the draft, returns the server-side attachment
 * records so the parent can merge them into local state. Throws on
 * failure so the parent can decide to keep the drafts around (so the
 * tradie doesn't have to re-pick from the OS gallery).
 *
 *   const attachments = await uploadDraftAttachments("entry", entryId, files);
 *   setForm(f => ({...f, attachments: [...f.attachments, ...attachments]}));
 *   setDraftFiles([]);
 */
export async function uploadDraftAttachments(kind, parentId, files) {
  if (!parentId || !files || !files.length) return [];
  const endpoint = kind === "entry"
    ? `/entries/${parentId}/attachments`
    : `/jobs/${parentId}/attachments`;
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  const { data } = await api.post(endpoint, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return Array.isArray(data?.added) ? data.added : [];
}
